import { CareerLevel, ConsultantNode, PayoutBreakdown, PayoutLineBreakdown } from '../types/career';
import {
  CO_OWNER_BONUS_RATE_UAH,
  DIRECTOR_BONUS_RATE_UAH,
  FOP_MILITARY_LEVY_RATE,
  FOP_MONTHLY_USV_UAH,
  FOP_SINGLE_TAX_RATE,
  getBaseRate,
  getRateDifference,
} from './rates';
import { isCoOwnerLevel, isDirectorLevel } from './careerRules';

export const DEFAULT_USD_EXCHANGE_RATE = 47.00;

export interface DirectBranchInput {
  id: string;
  name: string;
  level: CareerLevel;
  monthlyTurnoverAE: number;
}

/**
 * Calculates FOP 3rd group taxes on gross income.
 */
export function calculateFopTaxes(grossIncomeUAH: number): {
  singleTaxUAH: number;
  militaryLevyUAH: number;
  monthlyUsvUAH: number;
  totalTaxesUAH: number;
  netIncomeUAH: number;
} {
  const singleTaxUAH = grossIncomeUAH * FOP_SINGLE_TAX_RATE;
  const militaryLevyUAH = grossIncomeUAH * FOP_MILITARY_LEVY_RATE;
  const monthlyUsvUAH = grossIncomeUAH > 0 ? FOP_MONTHLY_USV_UAH : 0;
  const totalTaxesUAH = singleTaxUAH + militaryLevyUAH + monthlyUsvUAH;
  const netIncomeUAH = Math.max(0, grossIncomeUAH - totalTaxesUAH);

  return {
    singleTaxUAH,
    militaryLevyUAH,
    monthlyUsvUAH,
    totalTaxesUAH,
    netIncomeUAH,
  };
}

/**
 * Calculates complete payout breakdown for a consultant given their level, personal AE, and direct branches.
 */
export function calculatePayout(
  consultantId: string,
  level: CareerLevel,
  personalAE: number,
  directBranches: DirectBranchInput[],
  usdExchangeRate: number = DEFAULT_USD_EXCHANGE_RATE,
  commissionRateCoefficient: number = 0.8
): PayoutBreakdown {
  const mentorBaseRate = getBaseRate(level);
  const mentorNetRate = mentorBaseRate * commissionRateCoefficient;

  // 1. Personal Sales Commission
  const personalCommissionUAH = personalAE * mentorNetRate;

  // 2. Inter-Level Structural Commissions on Direct Lines
  let totalInterLevelCommissionUAH = 0;
  let directorBonusVolumeAE = personalAE;
  let coOwnerBonusVolumeAE = personalAE;
  const lineBreakdowns: PayoutLineBreakdown[] = [];

  for (const branch of directBranches) {
    const branchBaseRate = getBaseRate(branch.level);
    const rateDiff = getRateDifference(level, branch.level);
    const netRateDiff = rateDiff * commissionRateCoefficient;
    const lineCommission = branch.monthlyTurnoverAE * netRateDiff;

    totalInterLevelCommissionUAH += lineCommission;

    // Director bonus applies to sub-structures <= Level 5
    const qualifiesForDirectorBonus = branch.level <= 5;
    if (qualifiesForDirectorBonus) {
      directorBonusVolumeAE += branch.monthlyTurnoverAE;
    }

    // Co-owner bonus applies to sub-structures <= Level 7 (Levels 1-7 inclusive)
    const qualifiesForCoOwnerBonus = branch.level <= 7;
    if (qualifiesForCoOwnerBonus) {
      coOwnerBonusVolumeAE += branch.monthlyTurnoverAE;
    }

    lineBreakdowns.push({
      lineLeaderId: branch.id,
      lineLeaderName: branch.name,
      lineLeaderLevel: branch.level,
      lineMonthlyTurnoverAE: branch.monthlyTurnoverAE,
      mentorRate: mentorBaseRate,
      lineRate: branchBaseRate,
      rateDifferenceUAH: rateDiff,
      netRateDifferenceUAH: netRateDiff,
      lineCommissionUAH: lineCommission,
      qualifiesForDirectorBonus,
      qualifiesForCoOwnerBonus,
    });
  }

  // 3. Director Bonus (Levels 6, 7, 8: 50 UAH/E on personal + <= L5 branches)
  // Director bonus is ALWAYS paid in full (100%), never discounted by 80%
  const isDirector = isDirectorLevel(level);
  const directorBonusQualifiedTurnoverAE = isDirector ? directorBonusVolumeAE : 0;
  const directorBonusUAH = isDirector ? directorBonusQualifiedTurnoverAE * DIRECTOR_BONUS_RATE_UAH : 0;

  // 4. Co-Owner Bonus (Level 8: 100 UAH/E on personal + <= L7 branches)
  // Co-owner bonus is ALWAYS paid in full (100%), never discounted by 80%
  const isCoOwner = isCoOwnerLevel(level);
  const coOwnerBonusQualifiedTurnoverAE = isCoOwner ? coOwnerBonusVolumeAE : 0;
  const coOwnerBonusUAH = isCoOwner ? coOwnerBonusQualifiedTurnoverAE * CO_OWNER_BONUS_RATE_UAH : 0;

  // 5. Total Gross Income
  const totalGrossIncomeUAH =
    personalCommissionUAH +
    totalInterLevelCommissionUAH +
    directorBonusUAH +
    coOwnerBonusUAH;

  const totalGrossIncomeUSD = totalGrossIncomeUAH / usdExchangeRate;

  // 6. Tax Deductions
  const taxes = calculateFopTaxes(totalGrossIncomeUAH);
  const estimatedNetIncomeUSD = taxes.netIncomeUAH / usdExchangeRate;

  return {
    consultantId,
    level,
    personalAE,
    personalRateUAH: mentorBaseRate,
    rateCoefficient: commissionRateCoefficient,
    personalCommissionUAH,
    lineBreakdowns,
    totalInterLevelCommissionUAH,
    directorBonusQualifiedTurnoverAE,
    directorBonusUAH,
    coOwnerBonusQualifiedTurnoverAE,
    coOwnerBonusUAH,
    totalGrossIncomeUAH,
    totalGrossIncomeUSD,
    fopSingleTaxUAH: taxes.singleTaxUAH,
    militaryLevyUAH: taxes.militaryLevyUAH,
    monthlyUsvUAH: taxes.monthlyUsvUAH,
    totalTaxesUAH: taxes.totalTaxesUAH,
    estimatedNetIncomeUAH: taxes.netIncomeUAH,
    estimatedNetIncomeUSD,
  };
}

/**
 * Calculates payout breakdown directly from a ConsultantNode.
 */
export function calculateNodePayout(
  node: ConsultantNode,
  usdExchangeRate: number = DEFAULT_USD_EXCHANGE_RATE,
  commissionRateCoefficient: number = 0.8
): PayoutBreakdown {
  const directBranches: DirectBranchInput[] = node.children.map((child) => ({
    id: child.id,
    name: child.name,
    level: child.level,
    monthlyTurnoverAE: child.totalMonthlyAE,
  }));

  return calculatePayout(
    node.id,
    node.level,
    node.personalMonthlyAE,
    directBranches,
    usdExchangeRate,
    commissionRateCoefficient
  );
}
